
  import ScreenSize from '@/mixins/ScreenSize';
  import countFilterEvents from '@/apollo/queries/event/countFilterEvents.gql';
  import filterEvents from '@/apollo/queries/event/filterEvents.gql';
  import Pagination from '@/components/Pagination';
  import NoDataToShow from '@/components/elements/NoDataToShow';
  import EventCard from '@/components/elements/EventCard.vue';
  import EventFilter from '@/components/elements/EventFilter.vue';
  import EventFilterDialog from '@/components/filter-dialogs/EventFilterDialog.vue';
  import Events from '@/events/Events';

  export default {
    name: 'SectionEventListingWithFilter',
    components: {
      EventCard,
      EventFilter,
      EventFilterDialog,
      Pagination,
      NoDataToShow,
      RichText: () => import('@/components/elements/RichText'),
      CustomBigTitle: () => import('@/components/CustomBigTitle'),
    },
    mixins: [ScreenSize],
    props: {
      section: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data() {
      return {
        page: 1,
        countPages: 0,
        itemsPerPage: this.section.num_news_per_page,
        eventsCount: 0,
        orderBy: 'title:asc',

        events: [],

        filterParamsSet: false,
        filterParams: {
          search_term: null,
          search_city: null,
          date_from: '',
          date_until: '',
          categories: [],
        },
        triggerResetFilter: false,
        breadcrumbs: [
          {
            text: this.$t('page.index'),
            disabled: false,
            to: this.localePath({
              name: 'index',
            }),
            nuxt: true,
            exact: true,
          },
          {
            text: this.$t('page.events'),
            disabled: true,
            to: this.localePath({
              name: 'events',
            }),
            nuxt: true,
            exact: true,
          },
        ],

        orderByItems: [
          {
            value: 'date_from:desc',
            text: this.$t('orderBy.date.desc'),
          },
          {
            value: 'date_from:asc',
            text: this.$t('orderBy.date.asc'),
          },
          {
            value: 'title:desc',
            text: this.$t('orderBy.title.desc'),
          },
          {
            value: 'title:asc',
            text: this.$t('orderBy.title.asc'),
          },
        ],
      };
    },
    head() {
      const head = {meta: []};
      const query = Object.assign({}, this.$route.query);
      const canonical_links = [];

      canonical_links.push({
        rel: 'canonical',
        hid: 'canonical',
        href:
          process.env.NUXT_ENV_BASE_URL +
          this.localePath({
            query: {page: this.page > 1 ? this.page : undefined},
          }),
      });

      if (this.page > 1) {
        query.page = this.page - 1;
        canonical_links.push({
          rel: 'prev',
          hid: 'prev',
          href:
            process.env.NUXT_ENV_BASE_URL +
            this.localePath({
              query: query,
            }),
        });
      }

      if (this.page < this.countPages) {
        query.page = this.page + 1;
        canonical_links.push({
          rel: 'next',
          hid: 'next',
          href:
            process.env.NUXT_ENV_BASE_URL +
            this.localePath({
              query: query,
            }),
        });
      }

      head.link = canonical_links;

      if (this.page > 1) {
        head.meta.push({
          name: 'robots',
          content: 'noindex',
        });
        head.meta.push({
          name: 'googlebot',
          content: 'noindex',
        });
      }

      return head;
    },
    watch: {
      '$route.query': function () {
        this.parsePageFromQuery();
      },
    },
    created() {
      this.parsePageFromQuery();
      this.filterParamsSet = true;
      if (this.section.filterParams) {
        this.filterParams = this.section.filterParams;
      }
      // Parse filter params from URL so the initial Apollo query includes them
      // without relying on the child EventFilter's delayed emission
      const query = this.$route.query;
      if (query.search) {
        this.filterParams.search_term = query.search;
      }
      if (query.city) {
        this.filterParams.search_city = query.city;
      }
      if (query.datePeriodStart) {
        this.filterParams.date_from = query.datePeriodStart;
      }
      if (query.datePeriodEnd) {
        this.filterParams.date_until = query.datePeriodEnd;
      }
      if (query['category[]']) {
        const categories = query['category[]'];
        this.filterParams.categories = Array.isArray(categories)
          ? categories
          : [categories];
      }
    },
    apollo: {
      events: {
        query: filterEvents,
        variables() {
          const filterParams =
            !this.filterParamsSet && this.section.filterParams
              ? this.section.filterParams
              : this.filterParams;
          let searchTerm = [];
          if (filterParams.search_term) {
            const searchArray = filterParams.search_term.split(' ');
            searchTerm = this.removeEmptyStrings(searchArray);
          }
          const queryObj = {
            scope: this.section.content_scope,
            sort: this.orderBy,
            limit: this.itemsPerPage,
            start: this.itemsPerPage * (this.page - 1),
            date_today: this.$dayjs(new Date()).format('YYYY-MM-DD'),
            search_term: searchTerm,
            category_slugs: filterParams.categories,
          };
          if (filterParams.search_city) {
            queryObj.search_city = filterParams.search_city;
          }
          if (filterParams.date_from) {
            queryObj.date_from = filterParams.date_from
              ? new Date(filterParams.date_from).toISOString()
              : '';
          }
          if (filterParams.date_until) {
            queryObj.date_until = filterParams.date_until
              ? new Date(filterParams.date_until).toISOString()
              : '';
          }
          return queryObj;
        },
        result({stale, data}) {
          // Apollo calls this function multiple times, on the server and on the client at least once
          // If it's the first visit to this page then it will call it again on the client
          // If the client was on this page already then a cache exists and will be called before the actual network request is made
          // stale false indicates that this is the actual network call
          // @see https://github.com/vuejs/vue-apollo/discussions/492
          let items = [];
          if (data.events && data.events.length > 0) {
            items = data.events.map((item, key) => ({
              id: item.id,
              position: key + 1,
            }));
          }
          if (process.client && stale === false) {
            this.$emitWithClientData(Events.EventListingViewed, {
              itemsPerPage: this.itemsPerPage,
              items: items,
              page: this.page,
            });
          }
        },
      },
      eventsCount: {
        query: countFilterEvents,
        variables() {
          const filterParams =
            !this.filterParamsSet && this.section.filterParams
              ? this.section.filterParams
              : this.filterParams;
          let searchTerm = [];
          if (filterParams.search_term) {
            const searchArray = filterParams.search_term.split(' ');
            searchTerm = this.removeEmptyStrings(searchArray);
          }
          const filterObj = {
            scope: this.section.content_scope,
            date_today: this.$dayjs(new Date()).format('YYYY-MM-DD'),
            search_term: searchTerm,
            search_city: filterParams.search_city,
            category_slugs:
              filterParams.categories && filterParams.categories.length > 0
                ? filterParams.categories
                : null,
          };
          if (filterParams.date_from) {
            filterObj.date_from = filterParams.date_from
              ? new Date(filterParams.date_from).toISOString()
              : '';
          }
          if (filterParams.date_until) {
            filterObj.date_until = filterParams.date_until
              ? new Date(filterParams.date_until).toISOString()
              : '';
          }
          return filterObj;
        },
        result({data}) {
          this.eventsCount = data.eventsCount.aggregate.count;
          this.countPages = Math.ceil(this.eventsCount / this.itemsPerPage);
        },
      },
    },
    methods: {
      removeEmptyStrings(array) {
        return array.filter((str) => str !== '');
      },
      parsePageFromQuery() {
        this.page = this.$route.query.page
          ? parseInt(this.$route.query.page)
          : 1;
      },
      pushRouter() {
        const searchParams = new URLSearchParams();
        if (this.filterParams && this.filterParams.search_term) {
          searchParams.append('search', this.filterParams.search_term.trim());
        }
        if (this.filterParams && this.filterParams.search_city) {
          searchParams.append('city', this.filterParams.search_city);
        }
        if (this.filterParams && this.filterParams.date_from) {
          searchParams.append('datePeriodStart', this.filterParams.date_from);
        }
        if (this.filterParams && this.filterParams.date_until) {
          searchParams.append('datePeriodEnd', this.filterParams.date_until);
        }
        if (
          this.filterParams &&
          this.filterParams.categories &&
          this.filterParams.categories.length > 0
        ) {
          this.filterParams.categories.forEach((category) => {
            searchParams.append('category[]', category);
          });
        }
        let additionalParam = '';
        if (process.client) {
          const currentSearchParams = new URLSearchParams(
            window.location.search
          );
          currentSearchParams.forEach((value, key) => {
            if (key === 'mtm_group' && searchParams)
              additionalParam = '&mtm_group=' + value;
          });
        }
        this.$router.push(
          this.localePath({}) +
            `?${searchParams}` +
            `&page=${this.page}` +
            additionalParam +
            `#pb-event-filter-row`
        );
      },
      setFilterParams(params) {
        this.filterParams = params;
        this.page = 1;
        this.pushRouter();
      },
      resetFilterParams() {
        this.triggerResetFilter = !this.triggerResetFilter;
        this.filterParams = {};
        this.page = 1;
        this.pushRouter();
      },
    },
  };
